import { FC, useEffect, useState } from 'react';

import { faShip, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import { TabView, TabPanel } from 'primereact/tabview';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getCurrentDateTime } from '@/utils/getCurrentDateTime';
import { userHasPermission } from '@/utils/auth';

import TabHeader from '../TabHeader/TabHeader';
import ScheduleTable from './ScheduleTable/ScheduleTable';
import { getSchedule } from './getSchedule';
import ScheduleTableMobile from './ScheduleTableMobile/ScheduleTableMobile';
import { TableFilters } from './types';
import { filterData } from './filterData';
import SearchByShip from './SearchByShip/SearchByShip';

import styles from './Schedule.module.scss';

enum Tabs {
  List = 0,
  Search = 1,
}

const initialFilters: TableFilters = {
  shipName: '',
  service: '',
  startOperations: null,
  endOperations: null,
  openingStacking: null,
  closingStacking: null,
};

const Schedule: FC = () => {
  const { t } = useTranslation('schedule');
  const [activeIndex, setActiveIndex] = useState(0);

  const hasPermission = userHasPermission('AvanceDeBuque');

  const { data, isLoading: isLoadingSchedule } = useQuery({
    queryKey: ['schedule'],
    queryFn: getSchedule,
  });

  const [tableData, setTableData] = useState(data);
  const [filters, setFilters] = useState(initialFilters);

  const clearFilters = () => {
    setFilters(initialFilters);
  };

  useEffect(() => {
    if (data) {
      setTableData(filterData(data, filters));
    }
  }, [data, filters]);

  const lastUpdate = getCurrentDateTime();

  return (
    <TabView
      activeIndex={activeIndex}
      onTabChange={(e) => setActiveIndex(e.index)}
      style={{ position: 'relative' }}
    >
      <TabPanel
        header={
          <TabHeader
            active={activeIndex === Tabs.List}
            label={t('scheduleList')}
            icon={faShip}
          />
        }
      >
        <div className={styles.lastUpdate}>
          <p>{t('lastUpdate')}*:</p>
          <p>{lastUpdate}</p>
        </div>
        <ScheduleTable
          data={tableData}
          isLoading={isLoadingSchedule}
          showDetail={hasPermission}
        />
        <ScheduleTableMobile
          data={tableData}
          isLoading={isLoadingSchedule}
          filters={filters}
          setFilters={setFilters}
          clearFilters={clearFilters}
          showDetail={hasPermission}
        />
      </TabPanel>
      <TabPanel
        header={
          <TabHeader
            active={activeIndex === Tabs.Search}
            label={t('searchByShip')}
            icon={faMagnifyingGlass}
          />
        }
      >
        <div className={styles.lastUpdate}>
          <p>{t('lastUpdate')}*:</p>
          <p>{lastUpdate}</p>
        </div>
        <SearchByShip schedule={undefined} />
      </TabPanel>
    </TabView>
  );
};

export default Schedule;
